// HelpContentManager — admin-only CRUD for help_articles.
// Lives in Administration → Self-Service → Help Content.

import { useEffect, useState, useCallback } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";
import { Btn, Card, Inp, Modal } from "./primitives";

type ArticleSummary = {
  slug: string;
  title: string;
  section: string;
  section_order?: number;
  article_order?: number;
  is_public?: boolean;
};

type ArticleEdit = {
  slug?: string;
  title: string;
  section: string;
  section_order: number;
  article_order: number;
  body_markdown: string;
  role_visibility: string[];
  is_public: boolean;
  is_published: boolean;
};

const ROLES = ["admin", "lawyer", "legalops", "commercial_ops", "contract_manager", "business_reviewer", "requester", "executive"];

const blankArticle: ArticleEdit = {
  title: "",
  section: "",
  section_order: 100,
  article_order: 100,
  body_markdown: "",
  role_visibility: [],
  is_public: false,
  is_published: true,
};

export const HelpContentManager = () => {
  const [articles, setArticles] = useState<ArticleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ArticleEdit | null>(null);
  const [editingOriginalSlug, setEditingOriginalSlug] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string>("");
  const [err, setErr] = useState<string>("");
  const [confirmDelete, setConfirmDelete] = useState<ArticleSummary | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiCall("/help/articles");
      setArticles(r?.articles || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!editing || !editingOriginalSlug) { setPreviewHtml(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await apiCall(`/help/articles/${encodeURIComponent(editingOriginalSlug)}`);
        if (!cancelled && r?.article?.body_html) setPreviewHtml(r.article.body_html);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [editingOriginalSlug, editing]);

  const openNew = () => {
    setEditing({ ...blankArticle });
    setEditingOriginalSlug(null);
    setPreviewHtml("");
    setMsg(""); setErr("");
  };

  const openEdit = async (a: ArticleSummary) => {
    setMsg(""); setErr("");
    try {
      const r = await apiCall(`/help/articles/${encodeURIComponent(a.slug)}`);
      if (r?.article) {
        setEditing({
          slug: r.article.slug,
          title: r.article.title,
          section: r.article.section,
          section_order: r.article.section_order || 100,
          article_order: r.article.article_order || 100,
          body_markdown: r.article.body_markdown || "",
          role_visibility: r.article.role_visibility || [],
          is_public: !!r.article.is_public,
          is_published: r.article.is_published !== false,
        });
        setEditingOriginalSlug(r.article.slug);
        setPreviewHtml(r.article.body_html || "");
      }
    } catch (e: any) {
      setErr("Failed to load article for editing");
    }
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.title.trim() || !editing.section.trim() || !editing.body_markdown.trim()) {
      setErr("Title, section, and body are required.");
      return;
    }
    setSaving(true);
    setMsg(""); setErr("");
    try {
      let result;
      if (editingOriginalSlug) {
        result = await apiCall(`/help/articles/${encodeURIComponent(editingOriginalSlug)}`, {
          method: "PUT",
          body: JSON.stringify(editing),
        });
      } else {
        result = await apiCall("/help/articles", {
          method: "POST",
          body: JSON.stringify(editing),
        });
      }
      if (result?.article) {
        setMsg(editingOriginalSlug ? `Updated ${result.article.title}.` : `Created ${result.article.title}.`);
        setEditing(null);
        setEditingOriginalSlug(null);
        await refresh();
      } else {
        setErr(result?.error || "Save failed");
      }
    } catch (e: any) {
      setErr("Connection error");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      const r = await apiCall(`/help/articles/${encodeURIComponent(confirmDelete.slug)}`, { method: "DELETE" });
      if (r?.ok) {
        setMsg(`Deleted ${confirmDelete.slug}.`);
        await refresh();
      } else {
        setErr(r?.error || "Delete failed");
      }
    } catch {
      setErr("Connection error");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div data-deploy-marker="wave6-help-content-manager">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.tx }}>Help Content</div>
          <div style={{ fontSize: 11, color: T.txM, marginTop: 2 }}>Manage articles shown at /help (in-app) and /guide (public).</div>
        </div>
        <Btn v="primary" sz="sm" onClick={openNew}>+ New Article</Btn>
      </div>

      {msg && <div style={{ padding: "8px 12px", backgroundColor: T.grnS, color: T.grn, borderRadius: 5, marginBottom: 12, fontSize: 12 }}>{msg}</div>}
      {err && <div style={{ padding: "8px 12px", backgroundColor: T.redS, color: T.red, borderRadius: 5, marginBottom: 12, fontSize: 12 }}>{err}</div>}

      <Card>
        {loading ? (
          <div style={{ fontSize: 12, color: T.txM, padding: 20 }}>Loading...</div>
        ) : articles.length === 0 ? (
          <div style={{ fontSize: 12, color: T.txM, padding: 20, textAlign: "center" }}>No articles yet. Click &quot;+ New Article&quot; to create one.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.brd}` }}>
                <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, color: T.txM, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Title</th>
                <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, color: T.txM, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Section</th>
                <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 11, color: T.txM, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Public</th>
                <th style={{ textAlign: "right", padding: "8px 12px" }}></th>
              </tr>
            </thead>
            <tbody>
              {articles.map(a => (
                <tr key={a.slug} style={{ borderBottom: `1px solid ${T.brdSubtle}` }}>
                  <td style={{ padding: "8px 12px", fontSize: 12, color: T.tx }}>
                    <div style={{ fontWeight: 600 }}>{a.title}</div>
                    <div style={{ fontSize: 10, color: T.txD, fontFamily: "monospace" }}>{a.slug}</div>
                  </td>
                  <td style={{ padding: "8px 12px", fontSize: 12, color: T.txM }}>{a.section}</td>
                  <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 12, color: a.is_public ? T.grn : T.txD }}>{a.is_public ? "✓" : "—"}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>
                    <Btn v="ghost" sz="sm" onClick={() => openEdit(a)}>Edit</Btn>
                    <button onClick={() => setConfirmDelete(a)} style={{ fontSize: 10, color: T.red, background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit", marginLeft: 6 }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {editing && (
        <Modal open={!!editing} onClose={() => { setEditing(null); setEditingOriginalSlug(null); }} title={editingOriginalSlug ? `Edit: ${editing.title}` : "New Article"} w={900}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: "8px 0" }}>
            <div>
              <Inp label="Title" value={editing.title} onChange={(v: string) => setEditing({ ...editing, title: v })} />
              <Inp label="Section" value={editing.section} onChange={(v: string) => setEditing({ ...editing, section: v })} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <Inp label="Section Order" value={String(editing.section_order)} onChange={(v: string) => setEditing({ ...editing, section_order: parseInt(v, 10) || 100 })} />
                <Inp label="Article Order" value={String(editing.article_order)} onChange={(v: string) => setEditing({ ...editing, article_order: parseInt(v, 10) || 100 })} />
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 6 }}>Role Visibility (empty = all roles)</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {ROLES.map(role => {
                    const active = editing.role_visibility.includes(role);
                    return (
                      <button key={role} onClick={() => setEditing({
                        ...editing,
                        role_visibility: active ? editing.role_visibility.filter(r => r !== role) : [...editing.role_visibility, role],
                      })} style={{ padding: "4px 10px", borderRadius: 12, border: `1px solid ${active ? T.acc : T.brd}`, backgroundColor: active ? T.accS : T.bg3, color: active ? T.acc : T.txM, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                        {role}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, color: T.tx, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={editing.is_public} onChange={e => setEditing({ ...editing, is_public: e.target.checked })} />
                  Visible at /guide without authentication
                </label>
                <label style={{ fontSize: 12, color: T.tx, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={editing.is_published} onChange={e => setEditing({ ...editing, is_published: e.target.checked })} />
                  Published (uncheck to hide without deleting)
                </label>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>Body (Markdown)</div>
                <textarea value={editing.body_markdown} onChange={e => setEditing({ ...editing, body_markdown: e.target.value })} rows={20} style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "monospace", boxSizing: "border-box", resize: "vertical" }} />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.txM, marginBottom: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>Preview</div>
              <div style={{ padding: 16, backgroundColor: T.bg3, borderRadius: 6, border: `1px solid ${T.brd}`, minHeight: 400, fontSize: 13, lineHeight: 1.6, color: T.tx, overflow: "auto", maxHeight: 600 }}>
                {previewHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                ) : (
                  <div style={{ color: T.txM, fontStyle: "italic" }}>
                    {editingOriginalSlug ? "Loading preview..." : "Save the article once to see the rendered preview here."}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Btn v="secondary" sz="sm" onClick={() => { setEditing(null); setEditingOriginalSlug(null); }} disabled={saving}>Cancel</Btn>
            <Btn v="primary" sz="sm" onClick={save} disabled={saving}>{saving ? "Saving..." : (editingOriginalSlug ? "Save Changes" : "Create Article")}</Btn>
          </div>
        </Modal>
      )}

      {confirmDelete && (
        <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete Article" w={400}>
          <div style={{ padding: "12px 0" }}>
            <div style={{ fontSize: 12, color: T.tx, marginBottom: 16 }}>
              Permanently delete <strong>{confirmDelete.title}</strong>? This cannot be undone.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Btn v="secondary" sz="sm" onClick={() => setConfirmDelete(null)}>Cancel</Btn>
              <Btn v="primary" sz="sm" onClick={doDelete}>Delete</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
